import React, { useState, useEffect } from 'react';
import { useApp } from '../../contexts/AppContext';

const GeneralSettings = () => {
  const { showSuccess, showError } = useApp();
  const [establishmentName, setEstablishmentName] = useState('');
  const [establishmentIcon, setEstablishmentIcon] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      // Mock data - aquí se haría la llamada real a la API
      setEstablishmentName('Colegio San José');
      setIconPreview('/logo.png'); // Ruta del ícono actual
    } catch (error) {
      showError('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleIconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.startsWith('image/')) {
        showError('Por favor selecciona un archivo de imagen válido');
        return;
      }

      // Validar tamaño (máximo 2MB)
      if (file.size > 2 * 1024 * 1024) {
        showError('El archivo debe ser menor a 2MB');
        return;
      }

      setEstablishmentIcon(file);

      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setIconPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveIcon = () => {
    setEstablishmentIcon(null);
    setIconPreview(null);
  };

  const handleSave = async () => {
    if (!establishmentName.trim()) {
      showError('El nombre del establecimiento es requerido');
      return;
    }

    try {
      setSaving(true);

      // Aquí se enviaría a la API
      // Si hay un nuevo ícono, se subiría primero
      const formData = new FormData();
      formData.append('name', establishmentName);
      if (establishmentIcon) {
        formData.append('icon', establishmentIcon);
      }

      // Simular guardado
      await new Promise(resolve => setTimeout(resolve, 1000));

      showSuccess('Configuración guardada correctamente');
    } catch (error) {
      showError('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Configuración General
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Configura el nombre e ícono del establecimiento que aparecerán en el sistema
        </p>
      </div>

      {/* Nombre del establecimiento */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nombre del Establecimiento *
        </label>
        <input
          type="text"
          value={establishmentName}
          onChange={(e) => setEstablishmentName(e.target.value)}
          className="w-full md:w-2/3 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          placeholder="Ej: Colegio San José"
        />
        <p className="text-xs text-gray-500 mt-2">
          Este nombre aparecerá en el header de la aplicación
        </p>
      </div>

      {/* Ícono del establecimiento */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Ícono del Establecimiento
        </label>

        <div className="flex items-start space-x-6">
          {/* Preview del ícono */}
          <div className="flex-shrink-0">
            <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50">
              {iconPreview ? (
                <img
                  src={iconPreview}
                  alt="Preview del ícono"
                  className="w-full h-full object-contain rounded-lg"
                />
              ) : (
                <div className="text-center">
                  <svg className="w-12 h-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xs text-gray-500 mt-2">Sin ícono</p>
                </div>
              )}
            </div>
          </div>

          {/* Controles */}
          <div className="flex-1">
            <div className="space-y-3">
              <div>
                <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  Seleccionar imagen
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleIconChange}
                  />
                </label>
              </div>

              {iconPreview && (
                <button
                  onClick={handleRemoveIcon}
                  className="inline-flex items-center px-4 py-2 bg-white border border-red-300 rounded-lg text-sm font-medium text-red-700 hover:bg-red-50 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Eliminar imagen
                </button>
              )}
            </div>

            <div className="mt-4 text-xs text-gray-500 space-y-1">
              <p>• Formatos permitidos: PNG, JPG, SVG</p>
              <p>• Tamaño máximo: 2MB</p>
              <p>• Dimensiones recomendadas: 200x200 píxeles</p>
            </div>
          </div>
        </div>
      </div>

      {/* Botones de acción */}
      <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
        <button
          onClick={loadSettings}
          disabled={saving}
          className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !establishmentName.trim()}
          className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Guardando...</span>
            </>
          ) : (
            <span>Guardar Cambios</span>
          )}
        </button>
      </div>
    </div>
  );
};

export default GeneralSettings;
